import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ImageUploader from "@/components/uploads/ImageUploader";
import PhotoLightbox from "@/components/uploads/PhotoLightbox";

/**
 * The grid of job photos, wrapping ImageUploader rather than reimplementing it.
 *
 * It lived inline inside PostJob, which is where the second caller found it:
 * adding photos after publishing needs the same grid on the job page, and a
 * copy of it would have been a second place for the cap to drift. It had
 * already drifted once — the form stopped at five while the backend has always
 * accepted eight, so two photos were unreachable through the only surface that
 * could add them.
 *
 * Fully controlled: `urls` in, `onChange(next)` out. No internal state, so the
 * page that owns the list owns when it is saved — immediately in the wizard,
 * behind a Save button on the job page.
 *
 * The trailing tile is a *slot*, not a photo: it takes a selection, hands the
 * urls up, and empties itself (`resetAfterUpload`). Without that it kept
 * showing what it had just uploaded while the list below already held the same
 * image — one camera capture drawn twice, each copy with its own Replace and
 * Remove. See the note in ImageUploader. It also accepts several files at once,
 * capped at the room actually left.
 *
 * Props:
 *   urls: string[]                 current photo urls, in display order
 *   onChange: (next: string[]) => void
 *   max: number                    hard cap, default 8 (backend MAX_JOB_PHOTOS)
 *   disabled: boolean              hides the empty slot while saving
 *   testid: string                 data-testid prefix
 */
export const MAX_JOB_PHOTOS = 8;

export default function JobPhotoGrid({
  urls = [],
  onChange,
  max = MAX_JOB_PHOTOS,
  disabled = false,
  testid = "job-photo",
}) {
  const { t } = useTranslation();
  // Editing a job is also reading it: the owner of an open job only ever sees
  // this grid, so without this the one person who cannot expand a photo is the
  // one who took it.
  const [zoom, setZoom] = useState(null);

  const replaceAt = (index, url) => {
    const next = [...urls];
    next[index] = url;
    onChange(next);
  };
  const removeAt = (index) => onChange(urls.filter((_, i) => i !== index));
  // One call, however many files came in: the pages that own the list set it
  // with a plain `setState({...form, photo_urls: next})`, so appending in a
  // loop would have each iteration read the same stale `urls` and only the
  // last photo would survive.
  const appendAll = (items) => onChange([...urls, ...items.map((i) => i.url)]);
  const room = Math.max(0, max - urls.length);

  return (
    <>
    {/* N8. The cap was enforced and never stated: the trailing slot simply
        stopped being rendered at eight, so the person who tried to add a ninth
        photo met a grid that had quietly lost its Add tile and no sentence
        anywhere saying why. `ImageUploader` already refuses the overflow of a
        multi-file pick out loud (`uploader.tooMany`); what was missing is the
        number itself, before anyone runs into it.

        Here and not in `ImageUploader` because the cap belongs to the job, not
        to the control: this grid is the single surface behind all three places
        a job photo can be added — the wizard, the job page and Dany's card —
        so one line covers them. */}
    <p className="text-xs text-smoke mb-2 flex items-center gap-1"
       data-testid={`${testid}-count`}>
      {t("photos.count", { used: urls.length, max })}
      {room === 0 && (
        <span className="text-worker font-medium" data-testid={`${testid}-full`}>
          · {t("photos.full")}
        </span>
      )}
    </p>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`${testid}-grid`}>
      {urls.map((url, i) => (
        <div key={`${url}-${i}`} className="relative">
          <ImageUploader
            value={url}
            purpose="job_photo"
            aspect="square"
            label={t("common.addPhoto")}
            testid={`${testid}-${i}`}
            onUploaded={({ url: newUrl }) => replaceAt(i, newUrl)}
            onCleared={() => removeAt(i)}
            onExpand={() => setZoom(i)}
          />
        </div>
      ))}
      {room > 0 && !disabled && (
        <ImageUploader
          value={null}
          purpose="job_photo"
          aspect="square"
          label={t("common.addPhoto")}
          testid={`${testid}-new`}
          multiple
          maxFiles={room}
          resetAfterUpload
          onUploadedMany={appendAll}
        />
      )}
    </div>
    <PhotoLightbox urls={urls} index={zoom} onIndex={setZoom} onClose={() => setZoom(null)} />
    </>
  );
}
